/**
 * CarriedLineHero — D3.60 (Jun 7 2026)
 *
 * The genuine "first thing you see today" surface. When the member has a
 * carried line active (from the immersion's "Carry one with me today"
 * tap), this component takes over the very top of the home page for the
 * first 4 seconds:
 *
 *   • Forest-poster backdrop matching the Sacred Forest immersion
 *   • Gold tracked-uppercase eyebrow "Carrying with you today"
 *   • Large italic Cormorant Garamond line, centred
 *   • Gentle slow fade-in (1.6 s) then collapses gracefully into the
 *     standard TeamAlignmentHomeCard position below
 *
 * The same `/api/team-alignment/my/carried` endpoint that the home card
 * uses powers this hero, so they show the SAME line and both expire
 * at midnight UTC. After collapse the hero unmounts — only the quieter
 * inline card surfaces it the rest of the day.
 */
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const SACRED_FOREST_POSTER = `${API}/api/atelier-immersion/default_journey/poster.jpg`;

// 4-second hero hold, then a 1.4 s shrink + fade-out collapse.
const HERO_HOLD_MS  = 4000;
const COLLAPSE_MS   = 1400;

export default function CarriedLineHero() {
  const [carried, setCarried] = useState(null);
  const [phase,   setPhase]   = useState("hero");      // 'hero' | 'collapsing' | 'gone'

  // Fetch the active carried line once on mount.
  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem("reclaim_token");
    if (!token) return undefined;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/team-alignment/my/carried`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled && r.data?.carried?.line) setCarried(r.data.carried);
      } catch {
        /* silent — no carried line is the common case */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Drive the hero → collapsing → gone state machine once we have a
  // carried line. Two timers chained so the collapse animation has time
  // to play before we unmount completely.
  useEffect(() => {
    if (!carried) return undefined;
    const t1 = setTimeout(() => setPhase("collapsing"), HERO_HOLD_MS);
    const t2 = setTimeout(() => setPhase("gone"), HERO_HOLD_MS + COLLAPSE_MS);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [carried]);

  if (!carried || phase === "gone") return null;

  return (
    <AnimatePresence>
      <motion.section
        key="carried-hero"
        initial={{ opacity: 0, height: 0 }}
        animate={
          phase === "hero"
            ? { opacity: 1, height: "auto" }
            : { opacity: 0, height: 0 }
        }
        transition={{
          opacity: { duration: phase === "hero" ? 1.6 : 1.0, ease: [0.22, 1, 0.36, 1] },
          height:  { duration: phase === "hero" ? 0.9 : 0.9, ease: [0.22, 1, 0.36, 1] },
        }}
        className="relative overflow-hidden"
        style={{ marginTop: "-1rem" }}     // bleeds slightly into the header for hero feel
        data-testid="carried-line-hero"
      >
        {/* Forest poster backdrop — same image as the Immersion stage */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(rgba(7,13,11,0.65) 0%, rgba(3,6,10,0.85) 100%), url(${SACRED_FOREST_POSTER})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />

        {/* Top gold edge so the hero reads as "still emanating from the
            immersion" — a single 1-px gold line at the bottom. */}
        <div
          className="absolute bottom-0 left-0 right-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, rgba(215,187,122,0.55) 50%, transparent 100%)",
          }}
        />

        <div className="relative px-6 py-10 sm:py-14 max-w-2xl mx-auto text-center">
          <motion.p
            initial={{ opacity: 0, y: 6, letterSpacing: "0.5em" }}
            animate={{ opacity: 0.78, y: 0, letterSpacing: "0.4em" }}
            transition={{ duration: 1.4, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="uppercase mb-5"
            style={{
              color: "#D7BB7A",
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.6rem",
            }}
            data-testid="carried-line-hero-eyebrow"
          >
            Carrying with you today
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.8, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="italic"
            style={{
              color: "#F3EDDD",
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              fontSize: "clamp(1.4rem, 5vw, 2.2rem)",
              fontWeight: 300,
              lineHeight: 1.35,
              letterSpacing: "0.01em",
              textShadow: "0 0 32px rgba(0,0,0,0.65), 0 0 18px rgba(215,187,122,0.16)",
            }}
            data-testid="carried-line-hero-text"
          >
            {carried.line}
          </motion.p>

          {carried.team_name && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              transition={{ duration: 1.4, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
              className="italic mt-4"
              style={{
                color: "rgba(215, 187, 122, 0.65)",
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "0.85rem",
              }}
            >
              from {carried.team_name}
            </motion.p>
          )}
        </div>
      </motion.section>
    </AnimatePresence>
  );
}
